/**
 * 当月pt 内訳カード（要件定義pt / 開発pt の横棒）。
 *
 * **PDG エンジニアでは、基本情報カード（nickname / GitHub名 / 30d メトリクス /
 * 前月戦闘力比）をこのカードに差し替える。** my-page（本人）とエンジニアボードのメンバー詳細
 * (`/members/$email`、他人が見る) の両方で差し替わる。入れ替える理由は 2 つ:
 *
 * - nickname は my-page ヘッダーにも `/members/$email` の見出しにも既に出ており、
 *   同じ画面で二度読む情報だった
 * - 2026-10 の開発フロー変更で要件定義が開発ptと別管理になり、KPI の口径は
 *   「開発pt（要件定義を除く）」になった。全体 / チームの内訳は KPI 画面の
 *   「開発pt (MTD)」カードが出すが、**自分の当月pt のどれだけが要件定義だったか**は
 *   どこにも出ていなかった
 *
 * 口径は隣に並ぶ当月pt カードと同じ **当月の完了 + 処理中（現在スナップショット）**。
 * 揃えないと同じ行に並ぶ 2 枚のカードが違う合計を出す。
 *
 * 要件定義のバーはボタンで、押すと要件定義セット子票の件名（完了 + 処理中）が出る。
 * 「要件定義に 12pt 使った」までしか言わないバーに対し、「何の案件のどの成果物か」を
 * その場で開けるようにするため。開発pt 側は当月pt カードのクリックで同じ明細が開くので
 * 二重にしない。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business PDG エンジニアの当月pt を要件定義pt（浅緑）と開発pt（橙黄）の横棒 1 本へ積み、pt 値と占比を目盛付きで出す。要件定義バーのクリックで要件定義セット子票の明細モーダルを開く。my-page（本人）とエンジニアボードのメンバー詳細（他人）の両方に出る
 * @graph-connects useMemberDevelopPoints [calls] 当月の完了pt 明細フック
 * @graph-connects useMemberWipPoints [calls] 処理中pt 明細フック（現在スナップショット）
 * @graph-connects buildMemberPtBreakdown [calls] 明細行を要件定義pt / 開発pt へ割る純関数
 * @graph-connects RequirementPtDetailModal [calls] 要件定義pt 明細モーダル
 * @graph-connects member-pt-colors [references] 要件定義（浅緑）/ 開発（橙黄）の主題色 SSOT
 */
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, CardContent } from '@cortex/web-design-system/components/ui/Card';
import { useMemberDevelopPoints, useMemberWipPoints } from '../../hooks/useMember.js';
import { formatNum } from './combat-power/combat-power-utils.js';
import { jstCurrentYm, monthEndOf } from '../../lib/fiscal-quarter.js';
import { buildMemberPtBreakdown, type MemberPtRow } from './member-pt-breakdown.js';
import { RequirementPtDetailModal } from './RequirementPtDetailModal.js';
import { DEVELOP_BAR_COLOR, REQUIREMENT_BAR_COLOR } from './member-pt-colors.js';

/** @graph-connects none */
interface MemberPtBreakdownCardProps {
  /** メンバーのメールアドレス */
  email: string;
}

/** 明細取得の上限。当月pt 明細モーダルと同じ値（1 人 1 ヶ月の子票数は十分下回る）。 @graph-connects none */
const DETAIL_LIMIT = 500;

/** 目盛（横軸）の刻み（%）。 @graph-connects none */
const AXIS_TICKS = [0, 25, 50, 75, 100] as const;

/**
 * バーの最小表示幅（%）。0 より大きい pt が幅 0 で消えると「実績があるのに無い」ように
 * 見えるので、1% 未満でも線として残す。
 * @graph-connects none
 */
const MIN_VISIBLE_PERCENT = 1.5;

/** 占比 → バーの幅（%）。0 pt は 0 幅、0 より大きければ最小幅を確保する。 @graph-connects none */
function barWidth(pt: number, percent: number): number {
  if (pt <= 0) return 0;
  return Math.max(percent, MIN_VISIBLE_PERCENT);
}

/** 凡例の色ドット。 @graph-connects none */
function LegendDot({ color }: { color: string }) {
  return (
    <span
      className="inline-block size-2.5 shrink-0 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

/** @graph-connects none */
function LegendItem({
  color,
  label,
  pt,
  percent,
  total,
}: {
  color: string;
  label: string;
  pt: number;
  percent: number;
  /** 当月pt 合計。0 のときは占比を出さない（0% が並ぶだけで読めない） */
  total: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex items-baseline gap-2">
      <LegendDot color={color} />
      <span className="text-xs text-[var(--color-text-muted)]">{label}</span>
      <span className="text-sm font-semibold text-[var(--color-text-primary)]">
        {total > 0
          ? t('members.ptBreakdown.valueWithPercent', {
              value: formatNum(pt, 1),
              percent: formatNum(percent, 1),
            })
          : '-'}
      </span>
    </div>
  );
}

/** @graph-connects none */
export function MemberPtBreakdownCard({ email }: MemberPtBreakdownCardProps) {
  const { t } = useTranslation();
  const [detailOpen, setDetailOpen] = useState(false);

  // 当月（JST）の範囲。月プルダウンは持たない（隣の当月pt カードが月切替を担い、こちらは
  // 「いまの月の内訳」に絞る）。
  const currentYm = jstCurrentYm();
  const monthStart = `${currentYm}-01`;
  const monthLabel = `${String(parseInt(currentYm.slice(0, 4), 10))}/${String(parseInt(currentYm.slice(5, 7), 10))}`;

  const { data: completedData, isLoading: isCompletedLoading } = useMemberDevelopPoints(email, {
    startDate: monthStart,
    endDate: monthEndOf(monthStart),
    limit: DETAIL_LIMIT,
  });
  const { data: wipData, isLoading: isWipLoading } = useMemberWipPoints(email);

  const isLoading = isCompletedLoading || isWipLoading;

  const breakdown = useMemo(() => {
    const completedRows: MemberPtRow[] = (completedData?.data ?? []).map((d) => ({
      issueKey: d.issueKey,
      summary: d.summary,
      issueTypeName: d.issueTypeName,
      point: d.developPoint,
      completedDate: d.completedDate,
      parentIssueKey: d.parentIssueKey,
      parentSummary: d.parentSummary,
    }));
    const wipRows: MemberPtRow[] = (wipData?.data ?? []).map((d) => ({
      issueKey: d.issueKey,
      summary: d.summary,
      issueTypeName: d.issueTypeName,
      point: d.point,
      completedDate: null,
      parentIssueKey: d.parentIssueKey,
      parentSummary: d.parentSummary,
    }));
    return buildMemberPtBreakdown(completedRows, wipRows);
  }, [completedData, wipData]);

  const requirementWidth = barWidth(breakdown.requirementPt, breakdown.requirementPercent);
  const developWidth = Math.max(0, Math.min(breakdown.developPercent, 100 - requirementWidth));

  return (
    <>
      <Card className="animate-content-reveal flex h-full flex-col">
        <CardHeader
          title={t('members.ptBreakdown.title')}
          action={
            <span className="text-xs text-[var(--color-text-secondary)]">
              {t('members.ptBreakdown.total', { value: formatNum(breakdown.totalPt, 1) })}
            </span>
          }
        />
        <CardContent className="flex flex-1 flex-col justify-center gap-3">
          {isLoading ? (
            <div
              role="status"
              aria-label={t('members.quarterlyKpi.ptDetail.loading')}
              className="h-6 w-full animate-pulse rounded-full bg-[var(--color-bg-elevated)]"
            />
          ) : (
            <>
              {/* 積み上げ横棒。左=要件定義pt（浅緑・クリックで明細）/ 右=開発pt（橙黄） */}
              <div className="flex h-6 w-full overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
                <button
                  type="button"
                  data-testid="requirement-pt-bar"
                  aria-label={t('members.ptBreakdown.requirementOpenLabel')}
                  disabled={breakdown.requirementPt <= 0}
                  onClick={() => {
                    setDetailOpen(true);
                  }}
                  className="h-full transition-opacity hover:opacity-80 disabled:cursor-default disabled:hover:opacity-100"
                  style={{
                    width: `${String(requirementWidth)}%`,
                    backgroundColor: REQUIREMENT_BAR_COLOR,
                  }}
                />
                <div
                  data-testid="develop-pt-bar"
                  className="h-full"
                  style={{
                    width: `${String(developWidth)}%`,
                    backgroundColor: DEVELOP_BAR_COLOR,
                  }}
                />
              </div>

              {/* 横軸の目盛（0〜100%）。バーの長さを割合として読めるようにする */}
              <div
                className="flex justify-between text-[10px] text-[var(--color-text-muted)]"
                aria-hidden="true"
              >
                {AXIS_TICKS.map((tick) => (
                  <span key={tick}>{`${String(tick)}%`}</span>
                ))}
              </div>

              {/* 凡例（pt 値 + 占比） */}
              <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-x-6">
                <LegendItem
                  color={REQUIREMENT_BAR_COLOR}
                  label={t('members.ptBreakdown.requirement')}
                  pt={breakdown.requirementPt}
                  percent={breakdown.requirementPercent}
                  total={breakdown.totalPt}
                />
                <LegendItem
                  color={DEVELOP_BAR_COLOR}
                  label={t('members.ptBreakdown.develop')}
                  pt={breakdown.developPt}
                  percent={breakdown.developPercent}
                  total={breakdown.totalPt}
                />
              </div>

              {/* 注記は実績が無い月だけ。実績がある月はバーと凡例で読み取れるので足さない
                  （口径の説明は docs/pilab/portal.md 側に置く） */}
              {breakdown.totalPt <= 0 && (
                <p className="text-[11px] text-[var(--color-text-muted)]">
                  {t('members.ptBreakdown.emptyNote')}
                </p>
              )}
            </>
          )}
        </CardContent>
      </Card>
      <RequirementPtDetailModal
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false);
        }}
        monthLabel={monthLabel}
        completedRows={breakdown.requirementCompletedRows}
        wipRows={breakdown.requirementWipRows}
        isLoading={isLoading}
      />
    </>
  );
}
